// src/pages/TrackOrder.jsx
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

const TrackOrder = () => {
  const API_URL = process.env.REACT_APP_API_URL;

  const [orderId, setOrderId] = useState("");
  const [email, setEmail] = useState("");
  const [formError, setFormError] = useState("");

  const canSubmit = useMemo(() => {
    return orderId.trim().length > 0 && email.trim().length > 0;
  }, [orderId, email]);

  const {
    data,
    isFetching,
    isError,
    error,
    refetch,
    isFetched,
  } = useQuery({
    queryKey: ["track-order", orderId.trim(), email.trim().toLowerCase()],
    enabled: false, // ✅ manual trigger
    queryFn: async () => {
      const res = await fetch(`${API_URL}/order/track-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderId: orderId.trim(),
          email: email.trim().toLowerCase(),
        }),
      });

      const result = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(result.message || "Could not track order");
      return result;
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!canSubmit) {
      setFormError("Please enter your Order ID and the email used for the order.");
      return;
    }

    refetch();
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 py-8 sm:py-10">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-7">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            Track Your Order
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Enter your Order ID and the email you used during checkout.
          </p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Order ID</label>
              <input
                type="text"
                placeholder="e.g. 65f3c2..."
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                className="w-full mt-1 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-gray-800
                  focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-300"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">
                Email used for order
              </label>
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-1 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-gray-800
                  focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-300"
              />
            </div>

            {formError && <p className="text-sm text-red-600">{formError}</p>}

            <button
              type="submit"
              disabled={isFetching}
              className="w-full rounded-xl bg-gray-900 text-white py-3 font-semibold hover:bg-black
                disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isFetching ? "Tracking..." : "Track Order"}
            </button>
          </form>
        </div>

        {/* Loading */}
        {isFetching && (
          <div className="mt-5 text-sm text-gray-600">Loading...</div>
        )}

        {/* Error */}
        {isError && (
          <div className="mt-5 bg-white rounded-2xl border border-red-100 shadow-sm p-5">
            <p className="text-sm text-red-600">{error?.message}</p>
          </div>
        )}

        {/* Result */}
        {data && (
          <div className="mt-6 bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-7 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-gray-500">Order</p>
                <p className="text-base font-semibold text-gray-900 break-all">
                  {data.orderCode || data._id || "N/A"}
                </p>
              </div>

              <span className="self-start rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold capitalize text-gray-800">
                {data.orderStatus || "N/A"}
              </span>
            </div>

            <div className="flex items-center justify-between text-sm">
              <p className="text-gray-600">Total</p>
              <p className="font-semibold text-gray-900">
                ₦{Number(data.total || 0).toLocaleString()}
              </p>
            </div>

            {/* Items */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Items</h3>

              <div className="divide-y">
                {data.items?.map((item, idx) => (
                  <div
                    key={idx}
                    className="py-3 flex items-center justify-between gap-3"
                  >
                    <Link
                      to={item.product?._id ? `/product/${item.product._id}` : "#"}
                      className="flex items-center gap-3 min-w-0"
                    >
                      <img
                        src={item.product?.images?.[0] || item.product?.image || ""}
                        alt={item.product?.name || "Product"}
                        className="w-12 h-12 sm:w-14 sm:h-14 object-cover rounded-xl bg-gray-100 border border-gray-100 shrink-0"
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />

                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {item.product?.name || item.name || "Item"}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-500">
                          Qty: {item.quantity}
                        </p>
                      </div>
                    </Link>

                    <p className="font-semibold text-gray-900 shrink-0">
                      ₦{Number(item.finalPrice || 0).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Tracking */}
            {(data.trackingNumber || data.courier) && (
              <div className="pt-2 border-t">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Courier:</span>{" "}
                  {data.courier || "N/A"}
                </p>
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Tracking No:</span>{" "}
                  {data.trackingNumber || "N/A"}
                </p>

                {data.trackingLink && (
                  <a
                    href={data.trackingLink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex mt-2 text-sm text-blue-600 hover:text-blue-700 underline underline-offset-2 break-all"
                  >
                    Open Tracking Link
                  </a>
                )}
              </div>
            )}
          </div>
        )}

        {/* No result yet (after submit) */}
        {isFetched && !data && !isError && !isFetching && (
          <div className="mt-5 text-sm text-gray-600">
            No order data returned.
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackOrder;
